import React, { useEffect, useState } from 'react';

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { hexToRgb } from 'common/styles/colors';

const OutcomesLineChart = ({ lineChartData }) => {
  ChartJS.register(LineElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderColor: 'rgba(0, 0, 0, 1)',
        borderWidth: 1,
      },
    ],
  });

  useEffect(() => {
    if (lineChartData && lineChartData.length > 0) {
      const groupedData = {};
      const optionLabels = new Set();

      // First, determine all unique periods
      lineChartData.forEach(dataSet => {
        optionLabels.add(dataSet.name);
      });

      const allLabels = Array.from(optionLabels);

      // Initialize groupedData to ensure all labels exist for each option_name
      lineChartData.forEach(dataSet => {
        dataSet.data.forEach(item => {
          const { option_name, overall_score, color, accent } = item;

          if (!groupedData[option_name]) {
            groupedData[option_name] = {
              data: new Array(allLabels.length).fill(null),
              lineColor: color,
              accent: accent,
            };
          }

          // Assign values in the correct position
          const index = allLabels.indexOf(dataSet.name);
          if (index !== -1) {
            groupedData[option_name].data[index] = parseFloat(overall_score.toFixed(1));
          }
        });
      });

      // Format data for the chart
      const formattedData = Object.keys(groupedData).map((optionName, index) => ({
        label: optionName,
        data: groupedData[optionName].data,
        borderColor: hexToRgb(
          groupedData[optionName].lineColor,
          groupedData[optionName].accent === true ? 1 : 0.5
        ),
        backgroundColor: hexToRgb('#CCCCCC', 1.0),
        fill: false,
        tension: 0.3,
        pointBackgroundColor: hexToRgb(groupedData[optionName].lineColor, 1.0),
        pointBorderColor: '#777',
        pointHoverBorderColor: '#000',
        pointRadius: groupedData[optionName].accent === true ? 6 : 5,
        pointHoverRadius: 8,
        borderWidth: 2,
        borderDash: groupedData[optionName].accent === true ? [20, 5] : [],
      }));

      setChartData({
        labels: allLabels,
        datasets: formattedData,
      });
    }
  }, [lineChartData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          font: {
            family: 'Poppins',
            size: 12,
          },
        },
      },
      tooltip: {
        bodyFont: {
          family: 'Poppins',
          size: 12,
        },
        titleFont: {
          family: 'Poppins',
          size: 14,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: false,
          text: 'Quarters',
          font: {
            family: 'Poppins',
            size: 14,
          },
        },
        ticks: {
          font: {
            family: 'Poppins',
            size: 12,
          },
        },
      },
      y: {
        min: 0, // Set the minimum value for Y-axis
        max: 10, // Set the maximum value for Y-axis
        title: {
          display: true,
          text: 'Overall Score',
          font: {
            family: 'Poppins',
            size: 11,
          },
        },
        ticks: {
          font: {
            family: 'Poppins',
            size: 12,
          },
        },
      },
    },
  };

  return <Line data={chartData} options={options} />;
};

export default OutcomesLineChart;
